import { isEmpty } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  CartesianGrid,
  LabelList,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { labelListStyle } from '../../constants/line-label-styles';
import type { MonthlyComparisonData } from '../../data-transformer/compared-fr-line-chart/compared-fr-line.interface';
import {
  ComparedFrLineCommandFactory,
  type ComparedFrLineType,
} from '../../data-transformer/compared-fr-line-chart/compared-fr-line-command-factory';
import { DataExecutor } from '../../data-transformer/data-executor';
import type { DataCommandResponse } from '../../data-transformer/data-transformer.interface';
import { getColorsArray } from '../../helpers/get-colors-arrays';
import { getStatusColor } from '../../helpers/getStatusColor';
import { getValueOrDefault } from '../../helpers/getvalueOrDefault';
import { useComparedFrLineQuery } from '../../redux/config/chart-data';
import type { WidgetConfigurationData } from '../../redux/config/widgetApi';
import { getWidgets } from '../../redux/slices/layout-tabs-slice';
import CustomToolTip from '../CustomToolTip/CustomToolTip';
import ChartGenericWrapper from '../common/chart-generic-wrapper';
import { renderCustomizedLineLabel } from '../common/custom-label-list';
import { defaultLegendWrapperStyle, renderLegend } from '../common/custom-legend.component';
import { XAxisLineChart } from '../common/custom-X-axis.component';
import { ActiveDot } from '../common/line-active-dot.component';
import { getDotWithColor } from '../common/line-dot-base-props';
import { yAxisSettings } from '../common/y-axis-properties';
import type { CustomColorProps, TrifrProps } from '../WidgetSettingWindow/components/config-components.interfaces';
import type { ChartProps } from './chart.interface';
import type { LegendPayloadOnClick } from './chart-events';
import filteredKeys from './generalFunctions/filter-keys';
import { intervalCounter } from './generalFunctions/general-functions';
import { generateTicks } from './generalFunctions/generate-ticks';
import { filterChartValueByLegend } from './generalFunctions/get-domaincharts';
import { useXAxisTopSettings } from './hooks/use-x-axis-top-settings';
import useInvalidateChartResponse from './hooks/useInvalidateChartResponse';
import { useKpiConfigs } from './hooks/useKPIConfigs';
import { useKPIDomainValue } from './hooks/useKPIDomainValue';
import { useTRIFRConfigs } from './hooks/useRollingTRIFRConfigs';
import useStartEndDates from './hooks/useStartEndDates';
import { wrapToolTipElementContent } from './recharts-types';

export default function OdinComparedFrLineChart({ widgetId, layoutProps, activateAnimation }: ChartProps) {
  const [transformedData, setTransformedData] = useState<MonthlyComparisonData[]>([]);
  const [legendProps, setLegendProps] = useState<Record<string, boolean>>({});
  const [customColors, setCustomColors] = useState<Array<CustomColorProps>>([]);
  const [frProps, setFrProps] = useState<TrifrProps[]>();
  const [trifrProps, setTrifrProps] = useState<TrifrProps[]>();
  const [isKPI, setIsKPI] = useState<boolean>();
  const [configurationData, setConfigurationData] = useState<WidgetConfigurationData>();

  const { startDate, endDate } = useStartEndDates();
  const kpiConfigs = useKpiConfigs(configurationData);
  const xAxisSettings = useXAxisTopSettings();

  const filteredTransformData = useMemo(() => {
    return filterChartValueByLegend(transformedData, legendProps);
  }, [transformedData, legendProps]);

  const domainScale = useKPIDomainValue(configurationData, filteredTransformData);
  const ticks = generateTicks(domainScale);
  const trifrConfig = useTRIFRConfigs(configurationData);
  const handleRefetch = useInvalidateChartResponse(widgetId);
  const widgets = useSelector(getWidgets);

  const {
    data: widgetChartData,
    isLoading: isWidgetChartDataLoading,
    isFetching: isWidgetChartDataFetching,
    isError,
    error,
  } = useComparedFrLineQuery({
    widgetId,
    from: startDate,
    to: endDate,
  });

  const assignColors = useCallback((keys: string[]) => {
    return keys.map((item, index) => {
      return {
        color: getStatusColor(index),
        label: item,
      };
    });
  }, []);

  useEffect(() => {
    if (widgets) {
      setConfigurationData(widgets.find((widget) => +widget.widget.id === +widgetId)?.widget);
    }
  }, [widgets, widgetId]);

  useEffect(() => {
    if (widgetChartData && configurationData) {
      const commandExecutor = new DataExecutor();
      const commandFactory = new ComparedFrLineCommandFactory();
      let commandResponse: DataCommandResponse<ComparedFrLineType, unknown, ComparedFrLineType> | undefined;
      let outputData: ComparedFrLineType = widgetChartData;
      while ((commandResponse = commandFactory.getNextCommand()) !== undefined) {
        const inputData = outputData as MonthlyComparisonData[];
        outputData = commandExecutor.execute(commandResponse.command, inputData, configurationData);
      }

      setTransformedData(outputData as MonthlyComparisonData[]);

      if (widgetChartData.fr) {
        const keys = Object.keys(widgetChartData.fr).reverse();
        setFrProps(assignColors(keys));
      }
      if (trifrConfig.available) {
        const keys = Object.keys(widgetChartData.trifr).reverse();
        setTrifrProps(assignColors(keys));
      }
    }
  }, [widgetChartData, configurationData, trifrConfig.available, assignColors]);

  useEffect(() => {
    if (transformedData.length) {
      setIsKPI(transformedData.some((obj) => Object.hasOwn(obj, 'value_KPI')) && kpiConfigs.available);
      const secondFields = filteredKeys(transformedData);
      setCustomColors(getColorsArray(secondFields, configurationData));
    }
  }, [configurationData, kpiConfigs.available, transformedData]);

  const selectItem = useCallback(
    (e: LegendPayloadOnClick) => {
      const key = (e.dataKey as string | number | undefined) || e.value || '';
      setLegendProps({
        ...legendProps,
        [key]: !legendProps[key],
      });
    },
    [legendProps]
  );

  const labelList = useCallback((dataKey: string) => {
    return (
      <LabelList dataKey={dataKey} position="top" content={wrapToolTipElementContent(renderCustomizedLineLabel)} />
    );
  }, []);

  const frComponent = useMemo(() => {
    if (configurationData && frProps) {
      return (
        configurationData.widgetData.rollingFR &&
        frProps.map((item) => (
          <Line
            key={item.label}
            name={item.label}
            type="linear"
            dataKey={`fr${item.label}`}
            strokeWidth={2}
            stroke={item.color}
            fill={item.color}
            hide={legendProps[`fr${item.label}`]}
            dot={getDotWithColor(item.color)}
            activeDot={<ActiveDot item={item.label} />}
            isAnimationActive={activateAnimation}
          >
            {(configurationData.widgetConfig?.lineDisplayValue ||
              configurationData.widgetConfig?.lineDisplayValue === undefined) &&
              item.label === 'Current' &&
              labelList(`fr${item.label}`)}
          </Line>
        ))
      );
    }
  }, [configurationData, frProps, labelList, legendProps, activateAnimation]);

  const trifrComponent = useMemo(() => {
    const hideValues = trifrConfig?.displayValues === false;

    if (trifrProps && !isEmpty(trifrProps)) {
      return trifrProps.map((item) => (
        <Line
          key={item.label}
          name={item.label}
          type="linear"
          dataKey={`trifr${item.label}`}
          strokeWidth={2}
          stroke={item.color}
          fill={item.color}
          hide={legendProps[`trifr${item.label}`]}
          activeDot={<ActiveDot item={item.label} />}
          dot={getDotWithColor(item.color)}
          isAnimationActive={activateAnimation}
        >
          {!hideValues && item.label === 'Current' && labelList(`trifr${item.label}`)}
        </Line>
      ));
    }
  }, [trifrProps, trifrConfig, legendProps, labelList, activateAnimation]);

  const kpiComponent = useMemo(() => {
    if (kpiConfigs.available && isKPI) {
      const name = getValueOrDefault(kpiConfigs.caption, 'KPI');
      return (
        <Line
          name={name}
          type="linear"
          dataKey="value_KPI"
          strokeWidth={2}
          stroke={kpiConfigs.lineColor}
          fill={kpiConfigs.lineColor}
          activeDot={<ActiveDot item={name} />}
          hide={legendProps.value_KPI}
          dot={getDotWithColor(kpiConfigs.lineColor)}
          isAnimationActive={activateAnimation}
        >
          <LabelList dataKey="value_KPI" position="top" dy={-5} style={labelListStyle} />
        </Line>
      );
    }
  }, [kpiConfigs.available, kpiConfigs.caption, kpiConfigs.lineColor, isKPI, legendProps.value_KPI, activateAnimation]);

  return (
    <ChartGenericWrapper
      isError={isError}
      error={error}
      isFetching={isWidgetChartDataFetching}
      isLoading={isWidgetChartDataLoading}
      refetch={handleRefetch}
      widgetId={widgetId}
      widgetConfigurationData={configurationData && { ...configurationData, colors: customColors, layoutProps }}
      widgetChartData={widgetChartData}
      renderNoDataByDefault={!isWidgetChartDataFetching && !transformedData?.length}
    >
      {configurationData && widgetChartData && transformedData.length > 0 && (
        <ResponsiveContainer height="99%">
          <LineChart
            data={transformedData}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 10,
            }}
            layout="horizontal"
          >
            <Tooltip content={<CustomToolTip type="comparedFRLine" />} cursor={false} isAnimationActive={false} />

            <>
              <CartesianGrid vertical={false} horizontal strokeDasharray={undefined} />
              <XAxis
                {...xAxisSettings}
                interval={intervalCounter(transformedData)}
                tick={(props) => XAxisLineChart(props)}
                allowDuplicatedCategory
              />

              <YAxis
                {...yAxisSettings(domainScale, 'Frequency Rate', 0, -40)}
                ticks={ticks}
                domain={domainScale}
                allowDataOverflow={false}
              />

              <Legend
                formatter={renderLegend}
                verticalAlign="bottom"
                wrapperStyle={defaultLegendWrapperStyle}
                onClick={selectItem}
              />

              {/* KPI */}
              {kpiComponent}

              {/* TRIFR */}
              {trifrComponent}

              {/* FR */}
              {frComponent}
            </>
          </LineChart>
        </ResponsiveContainer>
      )}
    </ChartGenericWrapper>
  );
}
